import { isEmpty, isNil } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { TagOptions } from '@shared/components/filter/filterOptions/TagOptions';
import { ClosedView } from '@shared/components/filter/filterOptions/multiSelect/MultiSelect';
import { mapFilterByComponentToState } from '@shared/components/filter/state';
import {
  ControlOption,
  FilterOperation,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
  FilterTagSelection,
  tagSelectionToTagOption,
} from '@shared/components/filter/types';
import { MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput/MBTextInput';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { uuid } from '@shared/util/uuid';

interface OffsetOptionsProps {
  offset: API.CreateRecurrenceOffset | null;
  setOffset: (offset: API.CreateRecurrenceOffset | null) => void;
  tagFilter: API.FilterByComponentFragment | undefined;
  setTagFilter: (tagFilter: API.FilterByComponentFragment | undefined) => void;
  workflowTemplateId: string;
}

export function OffsetOptions({
  offset,
  setOffset,
  tagFilter,
  setTagFilter,
  workflowTemplateId,
}: OffsetOptionsProps) {
  const updateOffset = (key: keyof API.CreateRecurrenceOffset, value: any) => {
    setOffset({
      ...(offset ?? { value: 1, unit: 'DAY' }),
      [key]: value,
    });
  };

  const hasTagFilter = !isNil(tagFilter) && !isEmpty(tagFilter.value?.containsString?.values);

  return (
    <View paddingHorizontal={2} paddingTop={1} gap={2}>
      <TagFilter
        tagFilter={tagFilter}
        setTagFilter={setTagFilter}
        workflowTemplateId={workflowTemplateId}
      />
      {hasTagFilter && (
        <View gap={1}>
          <Text size="body1" weight="semibold">
            {i18n('workflowTemplate.component.dateAndRepeat.offset.createAnotherCardIn')}
          </Text>
          <View row>
            <MBTextInput
              value={String(offset?.value ?? '')}
              onChangeText={val => updateOffset('value', Number(val))}
              style={{
                width: grid(5.5),
                borderColor: Colors.v2.gray10,
                marginRight: grid(1.5),
              }}
            />
            <FrequencyMenu
              unit={offset?.unit}
              setUnit={val => updateOffset('unit', val)}
              value={offset?.value}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const unitLabels: Record<API.RecurrenceOffsetUnit, { singular: string; plural: string }> = {
  DAY: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.plural'),
  },
  WEEK: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.plural'),
  },
  MONTH: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.plural'),
  },
  YEAR: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.plural'),
  },
};

const units: API.RecurrenceOffsetUnit[] = ['DAY', 'WEEK', 'MONTH', 'YEAR'];

function FrequencyMenu({
  unit,
  setUnit,
  value,
}: {
  unit: API.RecurrenceOffsetUnit | undefined;
  setUnit: (unit: API.RecurrenceOffsetUnit) => void;
  value: number | undefined;
}) {
  const isSingular = value === 1;
  const label = isSingular ? unitLabels[unit ?? 'DAY'].singular : unitLabels[unit ?? 'DAY'].plural;

  return (
    <Menu>
      <Menu.Button flex label={label} />
      <Menu.Content>
        <Menu.Options
          options={units}
          onSelect={setUnit}
          getItemKey={item => item}
          getItemLabel={item => (isSingular ? unitLabels[item].singular : unitLabels[item].plural)}
        />
      </Menu.Content>
    </Menu>
  );
}

export function TagFilter({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
  maxTagDropdownWidth = 220,
}: {
  tagFilter?: API.FilterByComponentFragment;
  setTagFilter: (tagFilter?: API.FilterByComponentFragment) => void;
  workflowTemplateId: string;
  maxTagDropdownWidth?: number;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const tagComponents = workflowTemplate?.components.filter(isTagComponent);
  const tagComponentOptions: ControlOption[] = (tagComponents ?? []).map(component => ({
    id: component.id,
    label: component.label ?? '',
    icon: component.icon as MBIconV2Name,
  }));
  const selectedTagComponentOption = tagComponentOptions?.find(
    to => to.id === tagFilter?.componentId,
  );
  const selectedTagComponent = tagComponents?.find(c => c.id === tagFilter?.componentId);

  const isMultiSelect = selectedTagComponent?.multiselect;

  const filterUuidRef = useRef<string>(uuid());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }
    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label ?? '',
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    };
  }, [selectedTagComponent]);

  const selectedOptions = useMemo(() => {
    if (!filterOption) {
      return new Map<string, FilterSelectionState>();
    }

    const hasTagValues = Boolean(tagFilter?.value?.containsString?.values?.length);

    if (!hasTagValues) {
      return new Map<string, FilterSelectionState>();
    }

    const filterArray = tagFilter ? [tagFilter] : [];
    return mapFilterByComponentToState(filterArray, [filterOption]);
  }, [tagFilter, filterOption]);

  const [isComponentMenuOpen, setIsComponentMenuOpen] = useState(false);
  const [showComponentError, setShowComponentError] = useState(false);
  const [isTagOptionsMenuOpen, setIsTagOptionsMenuOpen] = useState(false);
  const [showTagOptionsError, setShowTagOptionsError] = useState(false);

  useEffect(() => {
    // if they open the menu, show errors on close
    if (isComponentMenuOpen) {
      setShowComponentError(true);
    }
  }, [isComponentMenuOpen]);

  useEffect(() => {
    // if they open the menu, show errors on close
    if (isTagOptionsMenuOpen) {
      setShowTagOptionsError(true);
    }
  }, [isTagOptionsMenuOpen]);

  const componentInvalid = isNil(selectedTagComponentOption);
  const tagOptionsInvalid = isEmpty(tagFilter?.value?.containsString?.values);
  const shouldShowComponentError = showComponentError && componentInvalid && !isComponentMenuOpen;
  const shouldShowTagOptionsError =
    showTagOptionsError && tagOptionsInvalid && !isTagOptionsMenuOpen;

  const handleTagComponentOptionChange = (option: ControlOption) => {
    // If the user selects the same tag component that is already selected, deselect it.
    if (option.id === tagFilter?.componentId) {
      setTagFilter(undefined);
      return;
    }

    setShowTagOptionsError(false);

    const newFilter: API.FilterByComponentFragment = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as API.FilterByComponentFragment;

    setTagFilter(newFilter);
  };

  const handleSetOption = (
    _filter: ControlOption,
    operation: FilterOperation,
    selection?: FilterSelection,
  ) => {
    if (operation !== 'updateSelections' || !selectedTagComponent) {
      return;
    }

    const selections = (selection as FilterTagOption[])?.filter(notNil) ?? [];

    // If all selections are cleared, reset the tag filter values
    if (isEmpty(selections)) {
      setTagFilter({
        componentId: selectedTagComponent.id,
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      } as API.FilterByComponentFragment);
      return;
    }

    const newFilterState: FilterSelectionState = {
      filter: filterOption,
      selection: selections as unknown as FilterSelection,
      negate: true,
    };

    const newTagFilter = getFilterComponent(newFilterState);
    if (newTagFilter) {
      setTagFilter(newTagFilter);
    }
  };

  const currentSelections = useMemo(() => {
    if (selectedOptions.size === 0) {
      return undefined;
    }
    return Array.from(selectedOptions.values())[0]?.selection as FilterTagSelection[];
  }, [selectedOptions]);

  const ItemComponent = ({ selection }: { selection: FilterTagOption }) => {
    if (!selection?.label) {
      return null;
    }
    return (
      <Tag
        color={selection.backgroundColor}
        label={selection.label}
        archived={selection.archived}
        style={{ maxWidth: grid(24) }}
      />
    );
  };

  return (
    <View gap={2}>
      <View gap={1}>
        <Text size="body1" weight="semibold">
          {i18n('workflowTemplate.component.dateAndRepeat.offset.whenTheField')}
        </Text>
        {/* Tag component selection */}
        <Menu isOpen={isComponentMenuOpen} onOpenChange={setIsComponentMenuOpen}>
          <Menu.Button
            icon={selectedTagComponentOption?.icon}
            label={selectedTagComponentOption?.label || i18n('card.actions.groups.noneSelected')}
            inactive={isNil(selectedTagComponentOption)}
          />
          <Menu.Content>
            <Menu.SearchableOptions
              options={tagComponentOptions}
              getItemKey={item => item.id}
              getItemIcon={item => item.icon}
              getItemLabel={item => item.label}
              onSelect={handleTagComponentOptionChange}
              isItemSelected={item => item.id === tagFilter?.componentId}
            />
          </Menu.Content>
        </Menu>
        {shouldShowComponentError && (
          <Text size="body2" danger>
            {i18n('card.form.errors.required')}
          </Text>
        )}
      </View>
      {/* Tag value selection */}
      {filterOption && (
        <View gap={1}>
          <Text size="body1" weight="semibold">
            {(isNil(currentSelections) || currentSelections.length <= 1) &&
              i18n('workflowTemplate.component.dateAndRepeat.offset.updatesTo')}
            {!isNil(currentSelections) &&
              currentSelections.length > 1 &&
              i18n('workflowTemplate.component.dateAndRepeat.offset.updatesToOneOf')}
          </Text>
          <View gap={0.5}>
            <Menu isOpen={isTagOptionsMenuOpen} onOpenChange={setIsTagOptionsMenuOpen}>
              {isEmpty(currentSelections) && <Menu.Button label={i18n('common.selectOptions')} />}
              {!isEmpty(currentSelections) && (
                <Menu.Button>
                  <ClosedView
                    selections={currentSelections?.map(tagSelectionToTagOption) || []}
                    ItemComponent={ItemComponent}
                    maxWidth={maxTagDropdownWidth}
                  />
                </Menu.Button>
              )}
              <Menu.Content>
                <View padding={2} paddingBottom={0}>
                  <TagOptions
                    filter={filterOption}
                    setOption={handleSetOption}
                    selectedOptions={selectedOptions}
                    includeEmptyOption={false}
                    isMultiSelect={!!isMultiSelect}
                  />
                </View>
              </Menu.Content>
            </Menu>
            {shouldShowTagOptionsError && (
              <Text size="body2" danger>
                {i18n('card.form.errors.required')}
              </Text>
            )}
          </View>
        </View>
      )}
    </View>
  );
}
